import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DataService} from './data.service';
import {FixedAssetModel} from '../DataModels/fixed-asset-model';
import {ResponseModel} from '../DataModels/response-model';

export interface FixedAssetSearch {
  searchText?: string;
  fromDate?: string | Date | null;
  toDate?: string | Date | null;
  activeOnly?: boolean;
  location_id?: string;
}

@Injectable({providedIn: 'root'})
export class FixedAssetsDataSourceService {
  // Align with backend route prefix created in API: /api/fixed-assets
  private apiController = 'fixed-assets';

  constructor(
    private api: DataService,
  ) {
  }

  /**
   * Preview depreciation schedule for the given draft asset.
   * Expects PascalCase payload per API contract and returns the raw API object (or res.Data)
   * containing ScheduleEntries array with PeriodStart/PeriodEnd/etc.
   */
  previewSchedule(draft: any): Observable<any> {
    // API expects POST to /api/fixed-assets/preview-schedule
    // Ensure no DepreciationRate is sent in preview payload (API is source of truth)
    const sanitized: any = (() => {
      try {
        const d = {...draft};
        if ('DepreciationRate' in d) {
          delete d.DepreciationRate;
        }
        if ('depreciationRate' in d) {
          delete d.depreciationRate;
        }
        return d;
      } catch {
        return draft;
      }
    })();
    return this.api.post(`${this.apiController}/preview-schedule`, sanitized).pipe(
      map((res: any) => {
        // try {
        //   console.log('[FixedAssetsDataSourceService][previewSchedule] payload', sanitized);
        //   console.log('[FixedAssetsDataSourceService][previewSchedule] raw response', res);
        // } catch {
        // }
        return (res?.Data ?? res);
      })
    );
  }

  // Note: list() with client-side mapping has been removed. The UI now consumes
  // the API payload directly via FixedAssetsService.listApi().

  getById(id: string): Observable<FixedAssetModel | undefined> {
    return this.api.getData(this.apiController, id).pipe(
      map((res: any) => {
        // try {
        //   console.log('[FixedAssetsDataSourceService][getById] raw response from API', {id, res});
        // } catch {
        // }
        const raw = (res?.Data ?? res) as any;
        if (!raw || typeof raw !== 'object') {
          return undefined;
        }
        // Normalize PascalCase (API) → camelCase (FA model)
        // Normalize disposal to the FE's nested shape and support both casings from API
        const rawDisposal: any = raw.Disposal ?? raw.disposal;
        const rawDisposalDate: string | undefined = raw.DisposalDate ?? raw.disposalDate ?? rawDisposal?.DisposalDate ?? rawDisposal?.disposalDate;
        const rawDisposalEntryDate: string | undefined = raw.DisposalEntryDate ?? raw.disposalEntryDate ?? rawDisposal?.DisposalEntryDate ?? rawDisposal?.disposalEntryDate ?? rawDisposal?.entryDate;
        const rawDisposalProceeds: number | undefined = raw.DisposalProceeds ?? raw.disposalProceeds ?? rawDisposal?.DisposalProceeds ?? rawDisposal?.disposalProceeds ?? rawDisposal?.proceeds;
        const rawDisposalExpenses: number | undefined = raw.DisposalExpenses ?? raw.disposalExpenses ?? rawDisposal?.DisposalExpenses ?? rawDisposal?.disposalExpenses ?? rawDisposal?.expenses;
        const normalizedDisposal = (rawDisposalDate != null || rawDisposalProceeds != null || rawDisposalExpenses != null || rawDisposal != null)
          ? {
            ...(rawDisposal || {}),
            // Normalize to camelCase fields used by the form/model
            disposalDate: rawDisposalDate ?? (rawDisposal ? (rawDisposal.disposalDate ?? rawDisposal.DisposalDate) : undefined),
            entryDate: rawDisposalEntryDate ?? (rawDisposal ? (rawDisposal.entryDate ?? rawDisposal.DisposalEntryDate ?? rawDisposal.disposalEntryDate) : undefined),
            proceeds: rawDisposalProceeds,
            expenses: rawDisposalExpenses,
          }
          : undefined;

        const a: FixedAssetModel = {
          id: String(raw.Id ?? raw.id ?? ''),
          name: raw.Name ?? raw.name ?? '',
          assetTag: raw.AssetTag ?? raw.assetTag,
          category: raw.Category ?? raw.category,
          description: raw.Description ?? raw.description ?? '',
          acquisitionDate: raw.AcquisitionDate ?? raw.acquisitionDate,
          inServiceDate: raw.InServiceDate ?? raw.inServiceDate,
          acquisitionCost: Number(raw.AcquisitionCost ?? raw.acquisitionCost ?? 0),
          salvageValue: raw.SalvageValue ?? raw.salvageValue,
          usefulLifeMonths: Number(raw.UsefulLifeMonths ?? raw.usefulLifeMonths ?? 0),
          depreciationMethod: (raw.DepreciationMethod ?? raw.depreciationMethod) as any,
          depreciationRate: raw.DepreciationRate ?? raw.depreciationRate,
          totalUnits: raw.TotalUnits ?? raw.totalUnits,
          companyId: raw.CompanyId ?? raw.companyId,
          companyName: raw.CompanyName ?? raw.companyName,
          locationId: raw.LocationId ?? raw.locationId,
          locationName: raw.LocationName ?? raw.locationName,
          entityId: raw.EntityId ?? raw.entityId,
          entityName: raw.EntityName ?? raw.entityName,
          assetAccountId: raw.AssetAccountId ?? raw.assetAccountId,
          accumulatedDepreciationAccountId: raw.AccumulatedDepreciationAccountId ?? raw.accumulatedDepreciationAccountId,
          depreciationExpenseAccountId: raw.DepreciationExpenseAccountId ?? raw.depreciationExpenseAccountId,
          gainOnDisposalAccountId: raw.GainOnDisposalAccountId ?? raw.gainOnDisposalAccountId,
          lossOnDisposalAccountId: raw.LossOnDisposalAccountId ?? raw.lossOnDisposalAccountId,
          // Proceeds Suspense account mapping from API → model
          proceedsSuspenseAccountId: raw.ProceedsSuspenseAccountId ?? raw.proceedsSuspenseAccountId,
          active: !!(raw.Active ?? raw.active ?? true),
          postedDepMonths: raw.PostedDepMonths ?? raw.postedDepMonths,
          lastPostedDepDate: raw.LastPostedDepDate ?? raw.lastPostedDepDate,
          disposal: normalizedDisposal,
          // Map schedule entries if provided from API (PascalCase → camelCase)
          schedule: this.mapSchedule(raw),
          netBookValue: Number(raw.NetBookValue ?? raw.netBookValue ?? 0),
          accumulatedDepreciation: Number(raw.AccumulatedDepreciation ?? raw.accumulatedDepreciation ?? 0),
        } as FixedAssetModel;
        // try {
        //   console.log('[FixedAssetsDataSourceService][getById] normalized asset for Fixed Asset Form', a);
        // } catch {
        // }
        return a;
      }),
      catchError((e) => {
        try {
          console.error('[FixedAssetsDataSourceService][getById] API error', e);
        } catch {
        }
        throw e;
      })
    );
  }

  // Map possible schedule shapes from API into the FE model
  private mapSchedule(raw: any): any[] {
    try {
      // Preferred shape: ScheduleEntries (PascalCase array)
      let src: any[] | undefined = raw?.ScheduleEntries;
      // Alternative shapes the API might return
      if (!Array.isArray(src)) {
        src = raw?.scheduleEntries;
      }
      if (!Array.isArray(src)) {
        src = raw?.schedule;
      }
      if (!Array.isArray(src)) {
        src = raw?.DepreciationScheduleEntries;
      }
      if (!Array.isArray(src)) {
        src = raw?.depreciationScheduleEntries;
      }
      if (!Array.isArray(src)) {
        return [];
      }

      return src.map((e: any, idx: number) => {
        const periodStart = e.PeriodStart ?? e.periodStart ?? e.Start ?? e.start ?? null;
        const periodEnd = e.PeriodEnd ?? e.periodEnd ?? e.End ?? e.end ?? null;
        // Ensure period number is populated for UI (# column). Default to index+1 when missing/zero.
        const pnRaw = e.PeriodNumber ?? e.periodNumber ?? e.Seq ?? e.seq ?? 0;
        const periodNumber = (Number(pnRaw) > 0 ? Number(pnRaw) : (idx + 1));
        return {
          periodStart,
          periodEnd,
          periodNumber,
          // Strictly use server-provided Book fields to avoid mismatches
          depreciationAmount: Number(e.BookDepreciation ?? 0),
          accumulatedDepreciation: Number(e.BookAccumulated ?? 0),
          endingBookValue: Number(e.EndingNBV ?? 0),
          // MACRS fields (if present)
          macrsDepreciation: e.MacrsDepreciation ?? e.macrsDepreciation ?? null,
          macrsAccumulated: e.MacrsAccumulated ?? e.macrsAccumulated ?? null,
        };
      });
    } catch (err) {
      try {
        console.warn('[FixedAssetsDataSourceService] mapSchedule failed', err);
      } catch {
      }
      return [];
    }
  }

  create(model: FixedAssetModel): Observable<FixedAssetModel> {
    // Backend provides POST upsert on /api/fixed-assets
    const payload = this.toApiModel(model);
    // try {
    //   console.log('[FixedAssetsDataSourceService][create] payload', payload);
    // } catch {
    // }
    return this.api.post(this.apiController, payload).pipe(
      map((res: any) => {
        // try {
        //   console.log('[FixedAssetsDataSourceService][create] raw response', res);
        // } catch {
        // }
        return (res?.Data ?? res) as FixedAssetModel;
      }),
      catchError((e) => {
        try {
          console.error('[FixedAssetsDataSourceService][create] API error', e);
        } catch {
        }
        throw e;
      })
    );
  }

  update(model: FixedAssetModel): Observable<FixedAssetModel> {
    // Use POST upsert semantics to match backend controller
    const payload = this.toApiModel(model);
    // try {
    //   console.log('[FixedAssetsDataSourceService][update] payload', payload);
    // } catch {
    // }
    return this.api.post(this.apiController, payload).pipe(
      map((res: any) => {
        // try {
        //   console.log('[FixedAssetsDataSourceService][update] raw response', res);
        // } catch {
        // }
        return (res?.Data ?? res) as FixedAssetModel;
      }),
      catchError((e) => {
        try {
          console.error('[FixedAssetsDataSourceService][update] API error', e);
        } catch {
        }
        throw e;
      })
    );
  }

  delete(id: string): Observable<boolean> {
    return this.api.deleteDataById(this.apiController, id).pipe(
      map(() => true)
    );
  }

  // --- helpers ---

  // Map FE model → API PascalCase model with ScheduleEntries
  private toApiModel(m: FixedAssetModel): any {
    const scheduleEntries = Array.isArray((m as any).schedule) ? (m as any).schedule.map((e: any) => ({
      PeriodStart: e.periodStart ?? e.PeriodStart ?? null,
      PeriodEnd: e.periodEnd ?? e.PeriodEnd ?? null,
      PeriodNumber: e.periodNumber ?? e.PeriodNumber ?? 0,
      BookDepreciation: e.depreciationAmount ?? e.BookDepreciation ?? 0,
      BookAccumulated: e.accumulatedDepreciation ?? e.BookAccumulated ?? 0,
      EndingNBV: e.endingBookValue ?? e.EndingNBV ?? 0,
      // Persist MACRS values when present
      MacrsDepreciation: e.macrsDepreciation ?? e.MacrsDepreciation ?? null,
      MacrsAccumulated: e.macrsAccumulated ?? e.MacrsAccumulated ?? null,
    })) : [];

    const dto: any = {
      Id: m.id,
      Name: m.name,
      AssetTag: m.assetTag,
      Category: m.category,
      Description: (m as any).description,
      CompanyId: (m as any).companyId,
      LocationId: (m as any).locationId,
      EntityId: (m as any).entityId,
      AcquisitionDate: m.acquisitionDate,
      InServiceDate: m.inServiceDate,
      AcquisitionCost: m.acquisitionCost,
      SalvageValue: (m as any).salvageValue,
      Active: m.active,
      UsefulLifeMonths: (m as any).usefulLifeMonths,
      DepreciationMethod: (m as any).depreciationMethod,
      TotalUnits: (m as any).totalUnits,
      UnitsPerMonthOverride: (m as any).unitsPerMonthOverride ?? null,
      AssetAccountId: (m as any).assetAccountId,
      AccumulatedDepreciationAccountId: (m as any).accumulatedDepreciationAccountId,
      DepreciationExpenseAccountId: (m as any).depreciationExpenseAccountId,
      GainOnDisposalAccountId: (m as any).gainOnDisposalAccountId,
      LossOnDisposalAccountId: (m as any).lossOnDisposalAccountId,
      // Proceeds Suspense account mapping model → API
      ProceedsSuspenseAccountId: (m as any).proceedsSuspenseAccountId,
      LastPostedDepDate: (m as any).lastPostedDepDate,
      DisposalDate: ((m as any).disposal?.disposalDate ?? (m as any).disposalDate ?? null),
      DisposalProceeds: ((m as any).disposal?.proceeds ?? (m as any).disposalProceeds ?? (m as any).disposalAmount ?? null),
      DisposalExpenses: ((m as any).disposal?.expenses ?? (m as any).disposalExpenses ?? null),
      ScheduleEntries: scheduleEntries,
    };
    return dto;
  }
}
